import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Trash2, BookOpen, Volume2, HelpCircle, Check, X, RotateCw, Gamepad2, BarChart3 } from "lucide-react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { VocabularyWord, VocabularyStats, ProficiencyLevel } from "@/types/vocabulary";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Vocabulary() {
  const { userProfile, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [stats, setStats] = useState<VocabularyStats>({ total: 0, unknown: 0, unsure: 0, known: 0 });
  const [loading, setLoading] = useState(true);

  const handleGoBack = () => {
    // 브라우저 히스토리를 사용하여 이전 페이지로
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/");
    }
  };

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      loadVocabulary();
      loadStats();
    }
  }, [isAuthenticated, userProfile]);

  const loadVocabulary = async () => {
    if (!userProfile) return;

    try {
      setLoading(true);
      const response = await apiRequest("GET", `/api/vocabulary/${userProfile.uid}`);

      if (!response.ok) {
        if (response.status === 503) {
          const errorData = await response.json();
          toast({
            title: "서비스 일시 중단",
            description: "단어장 서비스가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.",
            variant: "destructive",
          });
          setWords([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setWords(data as VocabularyWord[]);
    } catch (error) {
      console.error("Error loading vocabulary:", error);
      toast({
        title: "로딩 실패",
        description: "단어장을 불러오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      setWords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!userProfile) return;

    try {
      const response = await apiRequest("GET", `/api/vocabulary/${userProfile.uid}`);

      if (!response.ok) {
        setStats({ total: 0, unknown: 0, unsure: 0, known: 0 });
        return;
      }

      const data = await response.json();

      let unknown = 0, unsure = 0, known = 0;
      (data as VocabularyWord[]).forEach((word: any) => {
        const proficiency = word.proficiency;
        if (proficiency === "unknown") unknown++;
        else if (proficiency === "unsure") unsure++;
        else if (proficiency === "known") known++;
      });

      setStats({
        total: Array.isArray(data) ? data.length : 0,
        unknown,
        unsure,
        known
      });
    } catch (error) {
      console.error("Error loading stats:", error);
      setStats({ total: 0, unknown: 0, unsure: 0, known: 0 });
    }
  };

  const updateProficiency = async (wordId: string, proficiency: ProficiencyLevel) => {
    try {
      await apiRequest("PATCH", `/api/vocabulary/${wordId}/proficiency`, { proficiency });

      // Update local state
      setWords(words.map(w => w.id === wordId ? { ...w, proficiency } : w));

      // Update stats
      loadStats();

      toast({
        title: "숙련도 업데이트됨",
        description: "단어 숙련도가 업데이트되었습니다.",
      });
    } catch (error) {
      console.error("Error updating proficiency:", error);
      toast({
        title: "업데이트 실패",
        description: "숙련도 업데이트 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  const deleteWord = async (wordId: string) => {
    try {
      await apiRequest("DELETE", `/api/vocabulary/${wordId}`);

      // Update local state
      setWords(words.filter(w => w.id !== wordId));
      loadStats();

      toast({
        title: "단어 삭제됨",
        description: "단어가 단어장에서 삭제되었습니다.",
      });
    } catch (error) {
      console.error("Error deleting word:", error);
      toast({
        title: "삭제 실패",
        description: "단어 삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  const getProficiencyColor = (proficiency: ProficiencyLevel) => {
    switch (proficiency) {
      case "known": return "text-green-600 dark:text-green-400";
      case "unsure": return "text-yellow-600 dark:text-yellow-400";
      case "unknown": return "text-red-600 dark:text-red-400";
      default: return "text-muted-foreground";
    }
  };

  const getProficiencyLabel = (proficiency: ProficiencyLevel) => {
    switch (proficiency) {
      case "known": return "알고있음";
      case "unsure": return "아리송";
      case "unknown": return "모름";
      default: return "미분류";
    }
  };

  const chartData = [
    { name: "모름", value: stats.unknown, fill: "#ef4444" },
    { name: "아리송", value: stats.unsure, fill: "#eab308" },
    { name: "알고있음", value: stats.known, fill: "#22c55e" },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">로그인이 필요합니다</h2>
            <p className="text-muted-foreground text-center mb-6">
              단어장을 사용하려면 먼저 로그인해주세요.
            </p>
            <Link href="/">
              <Button>홈으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">단어장 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-6" onClick={handleGoBack} data-testid="button-back">
          <ArrowLeft className="h-4 w-4 mr-2" />
          이전으로
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">내 단어장</h1>
            <p className="text-muted-foreground">
              기사에서 저장한 단어들을 복습하고 숙련도를 관리하세요
            </p>
          </div>
          {words.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Link href="/flashcards">
                <Button variant="default" data-testid="button-flashcards" className="gap-2">
                  <RotateCw className="h-4 w-4" />
                  플래시카드
                </Button>
              </Link>
              {(userProfile?.role === "premium" || userProfile?.role === "platinum") && (
                <Link href="/analytics">
                  <Button variant="outline" data-testid="button-analytics" className="gap-2">
                    <BarChart3 className="h-4 w-4" />
                    학습 분석
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Statistics Dashboard */}
        {words.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    총 단어 수
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.total}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-green-600 dark:text-green-400">
                    알고있음
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {stats.known}
                  </div>
                  <Progress
                    value={stats.total > 0 ? (stats.known / stats.total) * 100 : 0}
                    className="mt-2"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                    아리송
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                    {stats.unsure}
                  </div>
                  <Progress
                    value={stats.total > 0 ? (stats.unsure / stats.total) * 100 : 0}
                    className="mt-2"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-red-600 dark:text-red-400">
                    모름
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {stats.unknown}
                  </div>
                  <Progress
                    value={stats.total > 0 ? (stats.unknown / stats.total) * 100 : 0}
                    className="mt-2"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Chart */}
            <Card>
              <CardHeader>
                <CardTitle>숙련도 분포</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {words.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">저장된 단어가 없습니다</h2>
              <p className="text-muted-foreground text-center mb-6">
                영어 기사에서 단어를 더블클릭하여 단어장에 추가하세요
              </p>
              <Link href="/">
                <Button>기사 보러가기</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {words.map((word) => (
              <Card key={word.id} className="hover-elevate" data-testid={`card-word-${word.id}`}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-2xl font-bold">
                        {word.word}
                      </CardTitle>
                      <Badge
                        variant="outline"
                        className={getProficiencyColor(word.proficiency)}
                      >
                        {getProficiencyLabel(word.proficiency)}
                      </Badge>
                    </div>
                    {word.pronunciation && (
                      <p className="text-sm text-muted-foreground mb-2">
                        <Volume2 className="inline h-3 w-3 mr-1" />
                        {word.pronunciation}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteWord(word.id)}
                    data-testid={`button-delete-${word.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Definition */}
                  <div>
                    <h4 className="text-sm font-semibold mb-1">정의</h4>
                    <p className="text-sm">{word.definition}</p>
                  </div>

                  {/* Korean Translation */}
                  {word.koreanTranslation && (
                    <div>
                      <h4 className="text-sm font-semibold mb-1">한글 뜻</h4>
                      <p className="text-sm">{word.koreanTranslation}</p>
                    </div>
                  )}

                  {/* Example Sentence */}
                  {word.exampleSentence && (
                    <div>
                      <h4 className="text-sm font-semibold mb-1">예문</h4>
                      <p className="text-sm italic">{word.exampleSentence}</p>
                    </div>
                  )}

                  {/* Synonyms */}
                  {word.synonyms && word.synonyms.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-1">동의어</h4>
                      <div className="flex flex-wrap gap-2">
                        {word.synonyms.map((syn, idx) => (
                          <Badge key={idx} variant="secondary">
                            {syn}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Antonyms */}
                  {word.antonyms && word.antonyms.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-1">반의어</h4>
                      <div className="flex flex-wrap gap-2">
                        {word.antonyms.map((ant, idx) => (
                          <Badge key={idx} variant="outline">
                            {ant}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Proficiency Buttons */}
                  <div className="pt-3 border-t border-border">
                    <h4 className="text-sm font-semibold mb-2">이 단어를 얼마나 알고 계신가요?</h4>
                    <div className="flex gap-2">
                      <Button
                        variant={word.proficiency === "unknown" ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateProficiency(word.id, "unknown")}
                        className="flex-1"
                        data-testid={`button-unknown-${word.id}`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        모름
                      </Button>
                      <Button
                        variant={word.proficiency === "unsure" ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateProficiency(word.id, "unsure")}
                        className="flex-1"
                        data-testid={`button-unsure-${word.id}`}
                      >
                        <HelpCircle className="h-4 w-4 mr-1" />
                        아리송
                      </Button>
                      <Button
                        variant={word.proficiency === "known" ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateProficiency(word.id, "known")}
                        className="flex-1"
                        data-testid={`button-known-${word.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        알고있음
                      </Button>
                    </div>
                  </div>

                  {/* Article Link */}
                  {word.articleId && (
                    <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
                      <span>출처: {word.articleTitle.substring(0, 40)}...</span>
                      <Link href={`/article/${word.articleId}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`link-article-${word.id}`}
                        >
                          기사 보기 →
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {words.length > 0 && (
          <div className="mt-8 p-4 bg-muted/50 rounded-md border border-border">
            <h3 className="font-semibold mb-2">💡 학습 팁</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• 단어의 숙련도를 표시하여 복습이 필요한 단어를 파악하세요</li>
              <li>• 동의어와 반의어를 함께 학습하면 어휘력이 더욱 향상됩니다</li>
              <li>• "모름"으로 표시된 단어를 우선적으로 복습하세요</li>
              <li>• 정기적으로 단어장을 방문하여 진도를 확인하세요</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
